/**
 * Global tracker for a running onboarding.
 *
 * Onboarding is a backend pipeline job that takes about 55 minutes, and until
 * now the only way back to it was remembering the URL. This composable polls
 * the log, feeds the pill (OnboardingProgressPill, mounted in the dashboard
 * layout), and raises one notification when the run ends.
 *
 * Deliberately the same shape as `useClientPackJob`: a module-level interval,
 * `useState` so every call site drives one tracker, `track()` to start and
 * `resume()` to pick the run up after a reload. A reader learns one behaviour,
 * not two.
 *
 * ⚠ A TOAST IS THE WRONG INSTRUMENT FOR THE RUNNING STATE. `addNotification`
 * removes a manual notification after 5 seconds, so for a job with fifty
 * minutes left the reader sees it once near the start and still has no way
 * back — which is the problem this exists to fix. The pill persists; the
 * toast fires once, at the end, carrying the action.
 */
import onboardingService from '@/api/services/onboarding'
import { useNotifications } from '@/composables/useNotifications'

const POLL_MS = 5000

// Module-level so every call site drives the same interval.
let pollTimer = null
let polling = false

// ⚠ KEYED ON THE JOB ID, NEVER A GLOBAL FLAG. A global "don't show the pill"
// would hide the NEXT onboarding too, and nobody would see one again without
// hunting for a setting. Keyed on the id, dismissing this run's pill silences
// this run and the next company still gets one.
const DISMISS_KEY = 'onboarding-pill-dismissed'

const readDismissed = () => {
  try {
    return localStorage.getItem(DISMISS_KEY)
  } catch {
    // Private mode, or storage disabled. Not being able to REMEMBER a
    // dismissal must not stop the pill working.
    return null
  }
}

const writeDismissed = (logId) => {
  try {
    localStorage.setItem(DISMISS_KEY, String(logId))
  } catch {
    /* see readDismissed */
  }
}

export const useOnboardingJob = () => {
  // { logId, companyName, label, done, total, status }
  const activeJob = useState('onboarding-job', () => null)
  const { addNotification } = useNotifications()

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  const isDismissed = (logId) => String(readDismissed() || '') === String(logId)

  /** The line the pill reads, taken from the log's own newest step. */
  const summarise = (detail) => {
    const items = [
      ...(detail.items || []),
      ...(detail.third_party_research?.items || []),
    ]
    const last = items.length ? items[items.length - 1] : null
    return {
      label: last?.message || last?.step_name || 'Starting…',
      done: items.filter((i) => i.status === 'completed').length,
      total: items.length,
    }
  }

  const finish = (job, detail) => {
    stopPolling()
    const name = job.companyName || detail.log?.company_name || 'The company'
    const status = detail.run_status || detail.log?.status
    if (status === 'completed') {
      const n = (detail.items || []).length
      addNotification(
        'Onboarding complete',
        `${name} · ${n} step${n === 1 ? '' : 's'} finished`,
        `/company-intelligence/${encodeURIComponent(name)}`,
        'onboarding',
        { persistent: true },
      )
    } else {
      addNotification(
        'Onboarding failed',
        `${name} · ${status || 'the run did not finish'}`,
        `/onboarding/${job.logId}`,
        'onboarding',
        { persistent: true, tone: 'critical' },
      )
    }
    activeJob.value = null
  }

  const pollOnce = async () => {
    const job = activeJob.value
    if (!job) { stopPolling(); return }
    if (polling) return
    polling = true
    try {
      const res = await onboardingService.getOnboardingLogDetail(job.logId)
      const current = activeJob.value
      if (!current || String(current.logId) !== String(job.logId)) return
      if (!res.success) {
        // A log that is gone or not ours stops the tracker; anything else is
        // a blip and the next poll retries.
        if (res.status === 404 || res.status === 403) {
          stopPolling()
          activeJob.value = null
        }
        return
      }
      const status = res.run_status || res.log?.status
      activeJob.value = {
        ...current,
        ...summarise(res),
        status,
        companyName: res.log?.company_name || current.companyName,
      }
      if (['completed', 'failed', 'error', 'cancelled'].includes(status)) {
        finish(activeJob.value, res)
      }
    } finally {
      polling = false
    }
  }

  /** Start tracking a run the user has just launched. */
  const track = (logId, companyName) => {
    if (!logId || isDismissed(logId)) return
    stopPolling()
    activeJob.value = { logId, companyName, label: 'Starting…', done: 0, total: 0, status: 'running' }
    pollOnce()
    pollTimer = setInterval(pollOnce, POLL_MS)
  }

  /**
   * Pick the user's in-flight onboarding up after a reload.
   *
   * ⚠ `running` ONLY, NEVER `submitted`. Measured on cloud dev: the log holds
   * 32 `submitted` rows against 4 `running`, and the known stuck ones have sat
   * in `submitted` since February — so resuming on that state would put a
   * spinning pill on a job that died eight months ago. A genuinely queued run
   * gets its pill from `track()`, because the person who pressed Launch is
   * still on the page.
   */
  const resume = async () => {
    if (activeJob.value) {
      if (!pollTimer) pollTimer = setInterval(pollOnce, POLL_MS)
      return
    }
    const res = await onboardingService.getOnboardingLogs(1, 'running')
    const log = res.success ? res.logs[0] : null
    if (log && !isDismissed(log.id)) track(log.id, log.company_name)
  }

  /**
   * Hide the pill for THIS run, and remember it across reloads.
   *
   * ⚠ Dismissing is not cancelling. The job carries on, the progress page is
   * still there, and the completion notification still fires — that one is the
   * terminal event, and suppressing it would mean a run that ends in silence.
   */
  const dismiss = () => {
    const job = activeJob.value
    if (!job) return
    writeDismissed(job.logId)
    stopPolling()
    activeJob.value = null
  }

  return { activeJob: readonly(activeJob), track, resume, dismiss }
}
